import type { ImagePickerAsset } from 'expo-image-picker';
import { Image } from 'expo-image';
import { Link, Redirect, router } from 'expo-router';
import { serverTimestamp } from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { Button } from '@/src/components/Button';
import { DateOfBirthPicker } from '@/src/components/DateOfBirthPicker';
import { ErrorView } from '@/src/components/ErrorView';
import { Input } from '@/src/components/Input';
import { LoadingView } from '@/src/components/LoadingView';
import { Screen } from '@/src/components/Screen';
import { useToast } from '@/src/components/Toast';
import { requestAccountDeletion } from '@/src/features/account-deletion/account-deletion-service';
import {
  onboardingSchema,
  type OnboardingValues,
} from '@/src/features/auth/validation';
import { completeCurrentRegistrationProfile } from '@/src/features/auth/auth-service';
import { useAuth } from '@/src/features/auth/use-auth';
import {
  acceptCurrentLegal,
  hasRecordedLegalAcceptance,
} from '@/src/features/legal/legal-service';
import {
  getProfileCoverPhotoURL,
  getProfileAvatarURL,
  claimUsername,
  hasCompletedOnboarding,
  hasCompletedOnboardingWithoutUsername,
  MAX_PROFILE_BIO_LENGTH,
  uploadProfileImageFromURL,
  updateUserProfile,
  type ProfileImageKind,
} from '@/src/features/profile/profile-service';
import { CityStateAutocomplete } from '@/src/features/profile/CityStateAutocomplete';
import { getCurrentProfileLocation } from '@/src/features/profile/location-service';
import { ProfileImageCropperModal } from '@/src/features/profile/ProfileImageCropperModal';
import {
  emptyGenderFormValue,
  genderOptions,
  genderValueToFormValue,
} from '@/src/features/profile/profile-form';
import {
  pickProfileImageAsset,
  usesProfileImageCropper,
} from '@/src/features/profile/profile-image-picker';
import {
  normalizeUsernameInput,
  usernameValidationMessage,
} from '@/src/features/profile/username';
import {
  emptyUsernameAvailability,
  UsernameAvailabilityField,
  type UsernameAvailability,
} from '@/src/features/profile/UsernameAvailabilityField';
import { PwaInstallCard } from '@/src/features/pwa/PwaInstallCard';
import {
  useProfile,
  useUpdateProfile,
  useUpdateProfileImage,
} from '@/src/features/profile/use-profile';
import { queryClient } from '@/src/lib/query-client';
import { useAppStore } from '@/src/state/app-store';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { isAtLeastAge } from '@/src/utils/age';
import { UserFacingError } from '@/src/utils/errors';

export default function OnboardingScreen() {
  const { setPassword, user } = useAuth();
  const { showError, showSuccess } = useToast();
  const profileQuery = useProfile(user?.uid);
  const updateProfile = useUpdateProfile(user?.uid);
  const updateProfileImage = useUpdateProfileImage(user?.uid);
  const pendingOnboardingFacebookPhoto = useAppStore(
    (state) => state.pendingOnboardingFacebookPhoto,
  );
  const appStoreHydrated = useAppStore((state) => state.hydrated);
  const pendingEmailVerificationRegistration = useAppStore(
    (state) => state.pendingEmailVerificationRegistration,
  );
  const setPendingEmailVerificationRegistration = useAppStore(
    (state) => state.setPendingEmailVerificationRegistration,
  );
  const setPendingOnboardingFacebookPhoto = useAppStore(
    (state) => state.setPendingOnboardingFacebookPhoto,
  );
  const [uploadingImageKind, setUploadingImageKind] = useState<
    'avatar' | 'cover' | null
  >(null);
  const [cropAsset, setCropAsset] = useState<ImagePickerAsset | null>(null);
  const [cropKind, setCropKind] = useState<ProfileImageKind | null>(null);
  const [imageUploadStatus, setImageUploadStatus] = useState<string | null>(
    null,
  );
  const [step, setStep] = useState<'details' | 'images'>('details');
  const hydratedUidRef = useRef<string | null>(null);
  const [avatarPreviewURL, setAvatarPreviewURL] = useState<string | null>(null);
  const [coverPreviewURL, setCoverPreviewURL] = useState<string | null>(null);
  const [externalAuthRepairCompleted, setExternalAuthRepairCompleted] =
    useState(false);
  const [locating, setLocating] = useState(false);
  const [usingProviderPhoto, setUsingProviderPhoto] = useState(false);
  const [usernameAvailability, setUsernameAvailability] =
    useState<UsernameAvailability>(emptyUsernameAvailability);
  const [claimingUsernameOnly, setClaimingUsernameOnly] = useState(false);
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [legalError, setLegalError] = useState<string | null>(null);
  const providerIds = user?.providerData.map((provider) => provider.providerId);
  const hasFacebookProvider = providerIds?.includes('facebook.com') ?? false;
  const hasGoogleProvider = providerIds?.includes('google.com') ?? false;
  const hasExternalAuthProvider = hasFacebookProvider || hasGoogleProvider;
  const hasPasswordProvider = providerIds?.includes('password') ?? false;
  const needsPassword =
    hasExternalAuthProvider &&
    (!hasPasswordProvider || !user?.email) &&
    !externalAuthRepairCompleted;
  const needsEmail = !user?.email && !externalAuthRepairCompleted;
  const needsExternalAccountSetup =
    hasExternalAuthProvider && (needsEmail || needsPassword);
  const needsRegistrationProfileRepair =
    profileQuery.isSuccess && !profileQuery.data;
  const pendingRegistration =
    pendingEmailVerificationRegistration?.uid === user?.uid
      ? pendingEmailVerificationRegistration
      : null;
  const shouldCompleteRegistrationProfile =
    needsExternalAccountSetup || needsRegistrationProfileRepair;
  const needsLegalAcceptance = !hasRecordedLegalAcceptance(
    profileQuery.data?.legal,
  );
  const completedOnboarding = hasCompletedOnboarding(profileQuery.data);
  const savedAvatarURL = getProfileAvatarURL(profileQuery.data, 'display');
  const savedCoverPhotoURL = getProfileCoverPhotoURL(profileQuery.data, 'hero');
  const pendingProviderPhotoURL =
    pendingOnboardingFacebookPhoto &&
    pendingOnboardingFacebookPhoto.uid === user?.uid
      ? pendingOnboardingFacebookPhoto.url
      : null;
  const savedFullName = profileQuery.data?.fullName?.trim() ?? '';
  const savedUsername = profileQuery.data?.username?.trim() ?? '';
  const savedDateOfBirth = profileQuery.data?.dateOfBirth ?? '';
  const suggestedProviderPhotoURL = hasExternalAuthProvider
    ? (pendingProviderPhotoURL ?? user?.photoURL ?? null)
    : null;
  const avatarURL =
    avatarPreviewURL ?? savedAvatarURL ?? suggestedProviderPhotoURL;
  const coverPhotoURL = coverPreviewURL ?? savedCoverPhotoURL;
  const avatarInitial =
    (profileQuery.data?.fullName ?? user?.displayName ?? 'F')
      .trim()
      .slice(0, 1)
      .toUpperCase() || 'F';
  const hasChosenAvatar = Boolean(avatarPreviewURL ?? savedAvatarURL);
  const canUseProviderPhoto = Boolean(suggestedProviderPhotoURL);
  const shouldShowFullNameField = savedFullName.length < 2;
  const shouldShowUsernameField =
    !savedUsername || Boolean(usernameValidationMessage(savedUsername));
  const shouldShowDateOfBirthField =
    !savedDateOfBirth || !isAtLeastAge(savedDateOfBirth);
  const hasSavedRegistrationDetails = Boolean(
    !shouldShowFullNameField &&
    !shouldShowUsernameField &&
    !shouldShowDateOfBirthField,
  );
  const needsUsernameOnly =
    !needsEmail &&
    !needsPassword &&
    hasCompletedOnboardingWithoutUsername(profileQuery.data) &&
    !savedUsername;
  const canUseCurrentUsername = Boolean(
    !shouldShowUsernameField || usernameAvailability.available,
  );
  const {
    control,
    formState: { errors, isSubmitting },
    getValues,
    handleSubmit,
    reset,
    setError,
    setValue,
  } = useForm<OnboardingValues>({
    defaultValues: {
      bio: null,
      dateOfBirth: '',
      email: user?.email ?? '',
      fullName: '',
      username: '',
      gender: emptyGenderFormValue(),
      location: null,
      password: '',
    },
  });

  useEffect(() => {
    const profile = profileQuery.data;

    if (
      !appStoreHydrated ||
      !user?.uid ||
      hydratedUidRef.current === user.uid
    ) {
      return;
    }

    if (!profile && pendingRegistration) {
      reset({
        bio: null,
        dateOfBirth: pendingRegistration.dateOfBirth,
        email: user.email ?? pendingRegistration.email,
        fullName: pendingRegistration.fullName,
        username: '',
        gender: emptyGenderFormValue(),
        location: null,
        password: '',
      });
      hydratedUidRef.current = user.uid;
      return;
    }

    if (!profile) {
      return;
    }

    reset({
      bio: profile.bio ?? null,
      dateOfBirth: profile.dateOfBirth ?? '',
      email: user.email ?? profile.email ?? '',
      fullName: profile.fullName ?? user.displayName ?? '',
      username: profile.username ?? '',
      gender: genderValueToFormValue(profile.gender?.value),
      location: profile.location ?? null,
      password: '',
    });
    hydratedUidRef.current = user.uid;
  }, [
    pendingRegistration,
    profileQuery.data,
    reset,
    appStoreHydrated,
    user?.displayName,
    user?.email,
    user?.uid,
  ]);

  useEffect(() => {
    setAvatarPreviewURL(null);
    setCoverPreviewURL(null);
    setExternalAuthRepairCompleted(false);
    setUsernameAvailability(emptyUsernameAvailability);
    setClaimingUsernameOnly(false);
    setAcceptedLegal(false);
    setLegalError(null);
    setStep('details');
  }, [user?.uid]);

  async function saveProfileImage(
    kind: ProfileImageKind,
    asset: ImagePickerAsset,
  ) {
    const imageLabel = kind === 'avatar' ? 'profile photo' : 'cover photo';

    setUploadingImageKind(kind);
    setImageUploadStatus(`Preparing ${imageLabel}...`);

    try {
      await updateProfileImage.mutateAsync({
        asset,
        kind,
        onUploadProgress: (progress) => {
          setImageUploadStatus(
            `Uploading ${imageLabel} ${Math.round(progress * 100)}%`,
          );
        },
      });
      if (kind === 'avatar') {
        setAvatarPreviewURL(asset.uri);
        setPendingOnboardingFacebookPhoto(null);
      } else {
        setCoverPreviewURL(asset.uri);
      }
      showSuccess(
        kind === 'avatar' ? 'Profile photo saved.' : 'Cover photo saved.',
      );
    } catch (error) {
      showError(
        error,
        `We could not prepare that ${kind === 'avatar' ? 'profile photo' : 'cover photo'}. Please try a JPEG, PNG, WebP, or iPhone photo under 200MB.`,
      );
    } finally {
      setImageUploadStatus(null);
      setUploadingImageKind(null);
    }
  }

  async function chooseProfileImage(kind: ProfileImageKind) {
    setUploadingImageKind(kind);

    try {
      const asset = await pickProfileImageAsset(kind);

      if (!asset) {
        return;
      }

      if (usesProfileImageCropper) {
        setCropAsset(asset);
        setCropKind(kind);
        return;
      }

      await saveProfileImage(kind, asset);
    } catch (error) {
      showError(
        error,
        `We could not prepare that ${kind === 'avatar' ? 'profile photo' : 'cover photo'}. Please try a JPEG, PNG, WebP, or iPhone photo under 200MB.`,
      );
    } finally {
      setUploadingImageKind(null);
    }
  }

  async function handleUseProviderPhoto() {
    if (!user?.uid || !suggestedProviderPhotoURL) {
      return;
    }

    setUsingProviderPhoto(true);

    try {
      const uploadResult = await uploadProfileImageFromURL({
        sourceURL: suggestedProviderPhotoURL,
        uid: user.uid,
      });

      await updateUserProfile(user.uid, {
        photoURL: uploadResult.downloadURL,
        profileImages: { avatar: uploadResult.profileImage },
      });
      setAvatarPreviewURL(uploadResult.downloadURL);
      setPendingOnboardingFacebookPhoto(null);
      await profileQuery.refetch();
      showSuccess('Account profile photo added.', 'Photo saved');
    } catch (error) {
      showError(error, 'Unable to use your account photo.');
    } finally {
      setUsingProviderPhoto(false);
    }
  }

  async function handleUseCurrentLocation() {
    setLocating(true);

    try {
      const displayLocation = await getCurrentProfileLocation();

      setValue('location', displayLocation, {
        shouldDirty: true,
        shouldValidate: true,
      });
      showSuccess('Your profile location was filled in.', 'Location added');
    } catch (error) {
      showError(error, 'Unable to use your current location.');
    } finally {
      setLocating(false);
    }
  }

  const saveRequiredDetails = handleSubmit(async (values) => {
    const mergedValues = {
      ...values,
      bio: values.bio?.trim() || null,
      dateOfBirth: values.dateOfBirth || savedDateOfBirth,
      email:
        values.email?.trim() || user?.email || profileQuery.data?.email || '',
      fullName:
        values.fullName?.trim() || savedFullName || user?.displayName || '',
      location: values.location?.trim() || null,
      username: normalizeUsernameInput(values.username || savedUsername),
    };
    const parsed = onboardingSchema.safeParse({
      ...mergedValues,
    });

    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => {
        const field = issue.path[0];

        if (
          field === 'dateOfBirth' ||
          field === 'email' ||
          field === 'fullName' ||
          field === 'username' ||
          field === 'gender' ||
          field === 'location' ||
          field === 'password'
        ) {
          setError(field, { message: issue.message });
        }
      });
      return;
    }

    setValue('dateOfBirth', parsed.data.dateOfBirth);

    if (!savedUsername && !usernameAvailability.available) {
      setError('username', {
        message:
          usernameAvailability.message ?? 'Choose an available username.',
      });
      return;
    }

    const onboardingEmail = parsed.data.email?.trim();

    if (needsEmail && !onboardingEmail) {
      setError('email', {
        message: 'Email is required to finish account setup.',
      });
      return;
    }

    if (!isAtLeastAge(parsed.data.dateOfBirth)) {
      try {
        await requestAccountDeletion('age_gate');
        showError(
          new Error('Frienzzi accounts are only available for ages 13 and up.'),
          'Account access blocked.',
        );
        router.replace('/account-deletion-pending');
      } catch (error) {
        showError(error, 'Unable to schedule account deletion.');
      }
      return;
    }

    if (needsLegalAcceptance && !acceptedLegal) {
      setLegalError(
        'You must agree to the Terms of Service and Privacy Policy to finish account setup.',
      );
      return;
    }

    try {
      if (needsPassword) {
        const password = parsed.data.password?.trim() ?? '';

        if (password.length < 8) {
          setError('password', {
            message: 'Password must be at least 8 characters.',
          });
          return;
        }

        await setPassword(password, onboardingEmail);
        setExternalAuthRepairCompleted(true);
      }

      let result: 'queued' | 'saved';
      const shouldUseRegistrationProfileCompletion =
        shouldCompleteRegistrationProfile || !savedUsername;

      if (shouldUseRegistrationProfileCompletion) {
        await completeCurrentRegistrationProfile({
          dateOfBirth: parsed.data.dateOfBirth,
          email: onboardingEmail ?? '',
          fullName: parsed.data.fullName,
          initialPhotoURL: null,
          inviteCode: pendingRegistration?.inviteCode ?? null,
          metaMethod:
            pendingRegistration?.authMethod ??
            (hasFacebookProvider
              ? 'facebook'
              : hasGoogleProvider
                ? 'google'
                : 'password'),
          username: parsed.data.username,
        });

        result = await updateProfile.mutateAsync({
          gender: parsed.data.gender,
          location: parsed.data.location,
        });
      } else {
        result = await updateProfile.mutateAsync({
          dateOfBirth: parsed.data.dateOfBirth,
          ...(needsEmail ? { email: onboardingEmail } : {}),
          fullName: parsed.data.fullName,
          gender: parsed.data.gender,
          location: parsed.data.location,
        });
      }

      if (needsLegalAcceptance && !shouldUseRegistrationProfileCompletion) {
        if (!user?.uid) {
          throw new Error('You must be signed in to accept legal terms.');
        }

        await acceptCurrentLegal({
          email: onboardingEmail ?? user.email ?? null,
          source: 'registration',
          uid: user.uid,
        });
      }

      if (result === 'queued') {
        showError(
          new UserFacingError('Connect to the internet to finish onboarding.'),
          'Onboarding not complete',
        );
        return;
      }

      await Promise.all([
        profileQuery.refetch(),
        queryClient.invalidateQueries({ queryKey: ['public-profile'] }),
      ]);
      if (pendingRegistration) {
        setPendingEmailVerificationRegistration(null);
      }
      showSuccess('Profile details saved.', 'Next step');
      setStep('images');
    } catch (error) {
      showError(error, 'Unable to save onboarding details.');
    }
  });

  async function claimMissingUsername() {
    const username = normalizeUsernameInput(getValues('username') ?? '');
    const validationMessage = usernameValidationMessage(username);

    if (validationMessage) {
      setError('username', { message: validationMessage });
      return;
    }

    if (
      !usernameAvailability.available ||
      usernameAvailability.username !== username
    ) {
      setError('username', {
        message:
          usernameAvailability.message ?? 'Choose an available username.',
      });
      return;
    }

    setClaimingUsernameOnly(true);

    try {
      await claimUsername(username);
      await Promise.all([
        profileQuery.refetch(),
        queryClient.invalidateQueries({ queryKey: ['public-profile'] }),
      ]);
      showSuccess('Your username is set.', 'Username claimed');
      router.replace('/home');
    } catch (error) {
      showError(
        error,
        'We could not claim that username. Please try another one.',
      );
    } finally {
      setClaimingUsernameOnly(false);
    }
  }

  async function completeOnboarding() {
    const bio = getValues('bio')?.trim() ?? '';

    if (bio.length > MAX_PROFILE_BIO_LENGTH) {
      setError('bio', {
        message: `Bio must be ${MAX_PROFILE_BIO_LENGTH} characters or fewer.`,
      });
      return;
    }

    if (!profileQuery.data?.username) {
      setStep('details');
      showError(
        new Error('Choose your username before finishing onboarding.'),
        'Username required',
      );
      return;
    }

    if (hasExternalAuthProvider && (needsEmail || needsPassword)) {
      setStep('details');
      showError(
        new Error('Add an email and password before finishing onboarding.'),
        'Account setup incomplete',
      );
      return;
    }

    try {
      const result = await updateProfile.mutateAsync({
        ...(bio ? { bio } : {}),
        onboarding: {
          completedAt: serverTimestamp(),
          ageVerifiedAt: serverTimestamp(),
          minimumAge: 13,
          source: hasFacebookProvider
            ? 'facebook'
            : hasGoogleProvider
              ? 'google'
              : 'email',
        },
      });

      if (result === 'queued') {
        showError(
          new UserFacingError('Connect to the internet to finish onboarding.'),
          'Onboarding not complete',
        );
        return;
      }

      await Promise.all([
        profileQuery.refetch(),
        queryClient.invalidateQueries({ queryKey: ['public-profile'] }),
      ]);
      setPendingOnboardingFacebookPhoto(null);
      showSuccess('Your profile is ready.', 'Onboarding complete');
      router.replace('/home');
    } catch (error) {
      showError(error, 'Unable to finish onboarding.');
    }
  }

  if (profileQuery.isLoading) {
    return <LoadingView message="Loading onboarding..." />;
  }

  if (profileQuery.isError) {
    return (
      <Screen title="Unable to load onboarding">
        <ErrorView
          message={profileQuery.error.message}
          onRetry={() => {
            void profileQuery.refetch();
          }}
        />
      </Screen>
    );
  }

  if (
    !appStoreHydrated ||
    (profileQuery.isFetching && !profileQuery.isFetchedAfterMount)
  ) {
    return <LoadingView message="Loading onboarding..." />;
  }

  if (
    completedOnboarding &&
    !needsExternalAccountSetup &&
    !needsRegistrationProfileRepair
  ) {
    return (
      <Redirect href={needsLegalAcceptance ? '/legal-consent' : '/home'} />
    );
  }

  if (needsUsernameOnly) {
    return (
      <Screen
        title="Choose your username"
        subtitle="Pick the public username for your Frienzzi profile link."
      >
        <View style={styles.form}>
          <View style={styles.usernameOnlyIntro}>
            <Text style={styles.usernameOnlyTitle}>
              Your public profile link
            </Text>
            <Text style={styles.locationHelp}>
              Usernames are unique and locked after you claim them.
            </Text>
          </View>
          <Controller
            control={control}
            name="username"
            render={({ field: { onBlur, onChange, value } }) => (
              <UsernameAvailabilityField
                error={errors.username?.message}
                onAvailabilityChange={setUsernameAvailability}
                onBlur={onBlur}
                onChange={onChange}
                value={value}
              />
            )}
          />
          <Button
            title="Claim username"
            disabled={!canUseCurrentUsername || claimingUsernameOnly}
            loading={claimingUsernameOnly}
            onPress={() => {
              void claimMissingUsername();
            }}
          />
        </View>
      </Screen>
    );
  }

  return (
    <>
      <Screen
        title="Finish your profile"
        subtitle={
          hasSavedRegistrationDetails
            ? 'Add the remaining profile details for your Frienzzi account.'
            : 'Frienzzi uses real full names and requires users to be 13 or older.'
        }
      >
        {step === 'details' ? (
          <View style={styles.form}>
            <Text style={styles.stepLabel}>Step 1 of 2</Text>
            {needsExternalAccountSetup ? (
              <View style={styles.setupNotice}>
                <Text style={styles.setupNoticeTitle}>
                  Finish account setup
                </Text>
                <Text style={styles.setupNoticeText}>
                  Add an email and password so this social login becomes a full
                  Frienzzi account.
                </Text>
              </View>
            ) : null}
            {needsEmail ? (
              <Controller
                control={control}
                name="email"
                render={({ field: { onBlur, onChange, value } }) => (
                  <Input
                    label="Email"
                    autoComplete="email"
                    keyboardType="email-address"
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="you@example.com"
                    textContentType="emailAddress"
                    value={value ?? ''}
                    error={errors.email?.message}
                  />
                )}
              />
            ) : null}
            {needsPassword ? (
              <Controller
                control={control}
                name="password"
                render={({ field: { onBlur, onChange, value } }) => (
                  <Input
                    label="Set a password"
                    autoComplete="new-password"
                    onBlur={onBlur}
                    onChangeText={onChange}
                    passwordRules="minlength: 8;"
                    secureTextEntry
                    textContentType="newPassword"
                    value={value ?? ''}
                    error={errors.password?.message}
                  />
                )}
              />
            ) : null}
            {shouldShowFullNameField ? (
              <Controller
                control={control}
                name="fullName"
                render={({ field: { onBlur, onChange, value } }) => (
                  <Input
                    label="Full name"
                    autoCapitalize="words"
                    onBlur={onBlur}
                    onChangeText={onChange}
                    textContentType="name"
                    value={value}
                    error={errors.fullName?.message}
                  />
                )}
              />
            ) : null}
            {shouldShowUsernameField ? (
              <Controller
                control={control}
                name="username"
                render={({ field: { onBlur, onChange, value } }) => (
                  <UsernameAvailabilityField
                    error={errors.username?.message}
                    onAvailabilityChange={setUsernameAvailability}
                    onBlur={onBlur}
                    onChange={onChange}
                    value={value}
                  />
                )}
              />
            ) : null}
            {shouldShowDateOfBirthField ? (
              <Controller
                control={control}
                name="dateOfBirth"
                render={({ field: { onBlur, onChange, value } }) => (
                  <DateOfBirthPicker
                    label="Date of birth"
                    onBlur={onBlur}
                    onChangeText={onChange}
                    value={value}
                    error={errors.dateOfBirth?.message}
                  />
                )}
              />
            ) : null}
            <Controller
              control={control}
              name="gender"
              render={({ field: { onChange, value } }) => (
                <View style={styles.field}>
                  <Text style={styles.label}>Gender</Text>
                  <View style={styles.optionGrid}>
                    {genderOptions.map((option) => {
                      const selected = value?.value === option.value;

                      return (
                        <Pressable
                          key={option.value}
                          accessibilityRole="button"
                          accessibilityState={{ selected }}
                          onPress={() => {
                            onChange(genderValueToFormValue(option.value));
                          }}
                          style={[
                            styles.option,
                            selected ? styles.optionSelected : null,
                          ]}
                        >
                          <Text
                            style={[
                              styles.optionText,
                              selected ? styles.optionTextSelected : null,
                            ]}
                          >
                            {option.label}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                  {errors.gender?.message ? (
                    <Text style={styles.error}>{errors.gender.message}</Text>
                  ) : null}
                </View>
              )}
            />
            <Controller
              control={control}
              name="location"
              render={({ field: { onBlur, onChange, value } }) => (
                <View style={styles.locationField}>
                  <CityStateAutocomplete
                    label="Location"
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="Start typing your city"
                    value={value ?? ''}
                    error={errors.location?.message}
                  />
                  <Button
                    title="Use current location"
                    variant="secondary"
                    loading={locating}
                    onPress={handleUseCurrentLocation}
                  />
                  <Text style={styles.locationHelp}>
                    Frienzzi saves only city, region, and country text to your
                    profile.
                  </Text>
                </View>
              )}
            />
            {needsLegalAcceptance ? (
              <View style={styles.legalField}>
                <Pressable
                  accessibilityLabel="Accept Terms of Service and Privacy Policy"
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: acceptedLegal }}
                  onPress={() => {
                    setAcceptedLegal((currentValue) => !currentValue);
                    setLegalError(null);
                  }}
                  style={styles.checkboxRow}
                >
                  <View
                    style={[
                      styles.checkbox,
                      acceptedLegal ? styles.checked : null,
                    ]}
                  >
                    {acceptedLegal ? (
                      <Text style={styles.checkmark}>✓</Text>
                    ) : null}
                  </View>
                  <Text style={styles.legalText}>
                    I agree to the{' '}
                    <Link href="/terms" style={styles.link}>
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link href="/privacy" style={styles.link}>
                      Privacy Policy
                    </Link>
                    .
                  </Text>
                </Pressable>
                {legalError ? (
                  <Text style={styles.error}>{legalError}</Text>
                ) : null}
              </View>
            ) : null}
            <Button
              title="Continue"
              disabled={!canUseCurrentUsername}
              loading={isSubmitting || updateProfile.isPending}
              onPress={saveRequiredDetails}
            />
          </View>
        ) : (
          <View style={styles.form}>
            <Text style={styles.stepLabel}>Step 2 of 2</Text>
            <View style={styles.profilePreview}>
              <View style={styles.coverPreview}>
                {coverPhotoURL ? (
                  <Image
                    accessibilityLabel="Cover photo preview"
                    contentFit="cover"
                    source={{ uri: coverPhotoURL }}
                    style={styles.coverPreviewImage}
                  />
                ) : (
                  <View style={styles.coverPreviewPlaceholder}>
                    <Text style={styles.coverPreviewText}>Cover photo</Text>
                  </View>
                )}
              </View>
              <View style={styles.avatarPreview}>
                {avatarURL ? (
                  <Image
                    accessibilityLabel="Profile photo preview"
                    contentFit="cover"
                    source={{ uri: avatarURL }}
                    style={styles.avatarPreviewImage}
                  />
                ) : (
                  <Text style={styles.avatarPreviewText}>{avatarInitial}</Text>
                )}
              </View>
            </View>
            <View style={styles.mediaActions}>
              {canUseProviderPhoto ? (
                <Button
                  title="Use account photo"
                  variant="secondary"
                  loading={usingProviderPhoto}
                  onPress={handleUseProviderPhoto}
                  style={styles.mediaButton}
                />
              ) : null}
              <Button
                title={
                  hasChosenAvatar ? 'Change profile photo' : 'Add profile photo'
                }
                variant="secondary"
                loading={uploadingImageKind === 'avatar'}
                onPress={() => {
                  void chooseProfileImage('avatar');
                }}
                style={styles.mediaButton}
              />
              <Button
                title={coverPhotoURL ? 'Change cover photo' : 'Add cover photo'}
                variant="secondary"
                loading={uploadingImageKind === 'cover'}
                onPress={() => {
                  void chooseProfileImage('cover');
                }}
                style={styles.mediaButton}
              />
            </View>
            {imageUploadStatus ? (
              <View style={styles.uploadStatus}>
                <ActivityIndicator color={colors.primary} size="small" />
                <Text style={styles.uploadStatusText}>{imageUploadStatus}</Text>
              </View>
            ) : null}
            <PwaInstallCard />
            <Controller
              control={control}
              name="bio"
              render={({ field: { onBlur, onChange, value } }) => (
                <View style={styles.bioField}>
                  <Input
                    label="Bio"
                    maxLength={MAX_PROFILE_BIO_LENGTH}
                    multiline
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="Share a little about yourself"
                    style={styles.bioInput}
                    textAlignVertical="top"
                    value={value ?? ''}
                    error={errors.bio?.message}
                  />
                  <Text style={styles.characterCount}>
                    {(value ?? '').length}/{MAX_PROFILE_BIO_LENGTH}
                  </Text>
                </View>
              )}
            />
            <Button
              title="Finish onboarding"
              loading={updateProfile.isPending}
              onPress={() => {
                void completeOnboarding();
              }}
            />
            <Button
              title="Skip for now"
              variant="secondary"
              disabled={updateProfile.isPending}
              onPress={() => {
                void completeOnboarding();
              }}
            />
            <Button
              title="Back"
              variant="secondary"
              disabled={updateProfile.isPending}
              onPress={() => {
                setStep('details');
              }}
            />
          </View>
        )}
      </Screen>
      <ProfileImageCropperModal
        asset={cropAsset}
        kind={cropKind}
        visible={Boolean(cropAsset && cropKind)}
        onCancel={() => {
          setCropAsset(null);
          setCropKind(null);
        }}
        onComplete={(asset) => {
          const kind = cropKind;

          setCropAsset(null);
          setCropKind(null);

          if (kind) {
            void saveProfileImage(kind, asset);
          }
        }}
      />
    </>
  );
}

const styles = StyleSheet.create({
  form: {
    gap: spacing.lg,
  },
  stepLabel: {
    color: colors.secondary,
    fontSize: 13,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  setupNotice: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.xs,
    padding: spacing.md,
  },
  setupNoticeTitle: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
  },
  setupNoticeText: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  field: {
    gap: spacing.sm,
  },
  label: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '700',
  },
  optionGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  option: {
    backgroundColor: colors.inputBackground,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  optionSelected: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  optionText: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '800',
  },
  optionTextSelected: {
    color: colors.surface,
  },
  error: {
    color: colors.danger,
    fontSize: 13,
  },
  legalField: {
    gap: spacing.sm,
  },
  checkboxRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
  },
  checkbox: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.secondary,
    borderRadius: 6,
    borderWidth: 2,
    height: 24,
    justifyContent: 'center',
    marginTop: 1,
    width: 24,
  },
  checked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  checkmark: {
    color: colors.surface,
    fontSize: 15,
    fontWeight: '900',
    lineHeight: 18,
  },
  legalText: {
    color: colors.mutedText,
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  link: {
    color: colors.secondary,
    fontWeight: '900',
  },
  locationField: {
    gap: spacing.sm,
  },
  usernameField: {
    gap: spacing.xs,
  },
  usernameOnlyIntro: {
    gap: spacing.xs,
  },
  usernameOnlyTitle: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
  },
  locationHelp: {
    color: colors.mutedText,
    fontSize: 13,
    lineHeight: 18,
  },
  mediaActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  bioField: {
    gap: spacing.xs,
  },
  bioInput: {
    minHeight: 112,
  },
  characterCount: {
    alignSelf: 'flex-end',
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '700',
  },
  mediaButton: {
    flexGrow: 1,
  },
  uploadStatus: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.sm,
    padding: spacing.md,
  },
  uploadStatusText: {
    color: colors.secondary,
    fontSize: 14,
    fontWeight: '800',
  },
  profilePreview: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
    paddingBottom: spacing.xl,
  },
  coverPreview: {
    aspectRatio: 592 / 164,
    backgroundColor: colors.surfaceTint,
    width: '100%',
  },
  coverPreviewImage: {
    height: '100%',
    width: '100%',
  },
  coverPreviewPlaceholder: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  coverPreviewText: {
    color: colors.mutedText,
    fontSize: 16,
    fontWeight: '800',
  },
  avatarPreview: {
    alignItems: 'center',
    alignSelf: 'center',
    backgroundColor: colors.primary,
    borderColor: colors.surface,
    borderRadius: 58,
    borderWidth: 4,
    height: 116,
    justifyContent: 'center',
    marginTop: -42,
    overflow: 'hidden',
    width: 116,
  },
  avatarPreviewImage: {
    height: '100%',
    width: '100%',
  },
  avatarPreviewText: {
    color: colors.surface,
    fontSize: 44,
    fontWeight: '900',
  },
});
